import type { ReactNode } from 'react';
import { Avatar, PollIcon, cn } from '@playerbase/ui';

import { AttachmentChip } from './attachment-chip.js';
import { DiscordMarkdown } from './discord-markdown.js';
import type { InboxMessage, InboxRow, ReactionTotal } from './use-feedback-inbox.js';

// ---------------------------------------------------------------------------
// One bundle of messages from the same author within a 5-minute window
// (most bundles are a single message). Header / reply-quote / stacked
// bodies / aggregate footer.
//
// Reactions render as actual emoji glyphs (unicode) or small custom-
// emoji images served from Discord's CDN. The reaction "that bumped
// the bundle" - flagged via reaction.isLatest - gets a tonal border so
// a viewer can spot what changed without reading a banner. The flag
// is data-derived (see use-feedback-inbox.ts), so the highlight
// survives a page reload.
//
// Reply rows embed a quote of the original message inline when the
// parent is on the current page. The container threads a
// Map<external_id, InboxMessage> in via byExternalId; off-page
// parents fall back to the existing "↪ reply" hint.
// ---------------------------------------------------------------------------

interface FeedbackRowProps {
  row: InboxRow;
  byExternalId: Map<string, InboxMessage>;
  // When set, the row gets a temporary ring + bg highlight - used by
  // the activity panel's click-to-source flow. Clears on its own once
  // the inbox container's highlight state expires.
  highlighted?: boolean | undefined;
  // Optional ref attached to the outer wrapper so the inbox container
  // can scrollIntoView() the exact row that was clicked from the
  // activity panel. Typed with `| undefined` because the prop chain
  // through FeedbackGroup / ForumPostCard passes the value through
  // verbatim, and exactOptionalPropertyTypes won't accept a hidden
  // `undefined` snuck through an optional prop slot.
  innerRef?: ((el: HTMLDivElement | null) => void) | undefined;
  // Suppresses the channel/thread span next to the username. The
  // activity panel renders that context above the row already, so
  // showing it inline duplicates the same string. The main feed
  // keeps it - that's the only place a row's channel is identified.
  hideChannelLabel?: boolean | undefined;
  // Suppresses the inline poll pill. PollGroupCard renders a rich
  // poll panel (question + answer breakdown + status) above the
  // FeedbackRow body, so the inline pill would duplicate the same
  // question text inside the same card.
  hidePollPill?: boolean | undefined;
  // Optional pre-rendered "voted: X, Y" detail line. Activity items
  // for poll-vote events pass this so the body of the row says what
  // was voted without the source-label band having to carry it.
  pollVoteDetail?: ReactNode | undefined;
}

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

function formatPostedAt(postedAt: string | null): string {
  if (postedAt === null) return '';
  const date = new Date(postedAt);
  const now = Date.now();
  const diff = now - date.getTime();
  if (diff >= 0 && diff < ONE_DAY_MS) {
    const minutes = Math.floor(diff / 60_000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  }
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function channelLabel(row: InboxRow): string {
  const name = row.discordChannelName ?? '(unknown channel)';
  if (row.discordParentChannelName !== null) {
    return `${name} in #${row.discordParentChannelName}`;
  }
  return `#${name}`;
}

function customEmojiUrl(id: string, animated: boolean | null): string {
  // Discord serves custom emoji from cdn.discordapp.com. Animated
  // emoji are gif; static are png. Falling back to png on null is
  // safe - a static frame still renders.
  const ext = animated === true ? 'gif' : 'png';
  return `https://cdn.discordapp.com/emojis/${id}.${ext}`;
}

export function ReactionPill({ reaction }: { reaction: ReactionTotal }): JSX.Element {
  const { emoji, count, isLatest } = reaction;
  const isCustom = emoji.id !== null;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-xs leading-none transition-colors',
        isLatest
          ? 'border-info bg-info-bg text-info-text ring-1 ring-info'
          : 'border-border bg-surface-2 text-text-muted',
      )}
    >
      {isCustom ? (
        <img
          src={customEmojiUrl(emoji.id!, emoji.animated)}
          alt={emoji.name ?? ''}
          // 16px tall keeps the pill at the same height as a text-xs
          // line so reaction rows don't jut taller than the message.
          className="h-4 w-4"
          loading="lazy"
        />
      ) : (
        <span aria-label={emoji.name ?? 'reaction'} className="text-sm leading-none">
          {emoji.name ?? '?'}
        </span>
      )}
      <span className="tabular-nums">{count}</span>
    </span>
  );
}

function ReplyQuote({ parent }: { parent: InboxMessage }): JSX.Element {
  // Trim the parent body so a long original doesn't dwarf the reply.
  // 140 chars is enough to recognise the message.
  const trimmed =
    parent.rawText.length > 140 ? `${parent.rawText.slice(0, 140).trimEnd()}…` : parent.rawText;
  // Polls are messages whose visible content is the poll question,
  // not raw_text - which is empty. Show the question with the PollIcon
  // so a reply to a poll reads "@ed · <chart> Which is it?" rather
  // than a quote with no text.
  const isPollParent = parent.poll !== null && parent.rawText.length === 0;
  const placeholder: ReactNode = isPollParent ? (
    <span className="inline-flex items-center gap-1 align-middle">
      <PollIcon className="h-3 w-3" />
      <span>{parent.poll!.question}</span>
    </span>
  ) : parent.rawText.length === 0 && parent.attachments.length > 0 ? (
    '(attachment)'
  ) : (
    trimmed
  );
  return (
    <div className="mt-1 mb-1 border-l-2 border-border pl-2 text-xs">
      <span className="font-semibold text-text-muted">
        {parent.authorHandle ?? 'unknown'}
      </span>
      <span className="text-text-muted"> · </span>
      <span className="text-text-muted whitespace-pre-wrap break-words">
        {placeholder}
      </span>
    </div>
  );
}

export function FeedbackRow({
  row,
  byExternalId,
  highlighted = false,
  innerRef,
  hideChannelLabel = false,
  hidePollPill = false,
  pollVoteDetail,
}: FeedbackRowProps): JSX.Element {
  const messages = row.messages;
  const latest = messages[messages.length - 1]!;

  // Flatten attachments across the bundle (most bundles are a single
  // message). Render order follows posting order, so the oldest message
  // in the bundle contributes its attachments first.
  const attachments = messages.flatMap((m) => m.attachments);

  const polls = hidePollPill ? [] : messages.filter((m) => m.poll !== null);
  const anyEdited = messages.some((m) => m.editedAt !== null);

  const showFooter =
    attachments.length > 0 ||
    polls.length > 0 ||
    row.reactions.length > 0 ||
    pollVoteDetail !== undefined;

  const replyParent =
    row.replyToExternalId !== null ? byExternalId.get(row.replyToExternalId) ?? null : null;

  return (
    <div
      ref={innerRef}
      className={cn(
        'px-4 py-3 transition-colors hover:bg-surface-2',
        // Highlight pulse: activity-panel click sets `highlighted` for
        // ~2.5s, the inbox container then clears it. Ring + bg-info-bg
        // mirrors the reaction-pill "isLatest" treatment so the same
        // visual language signals "this is what just happened".
        highlighted && 'bg-info-bg ring-2 ring-info ring-inset',
      )}
    >
      <div className="flex items-center gap-2 text-xs">
        <Avatar size="sm" name={row.authorHandle ?? '?'} />
        <span className="font-semibold text-sm text-text">
          {row.authorHandle ?? 'unknown'}
        </span>
        {!hideChannelLabel && (
          <span className="text-text-muted">· {channelLabel(row)}</span>
        )}
        {row.replyToExternalId !== null && replyParent === null && (
          <span className="text-text-muted">· ↪ reply</span>
        )}
        {anyEdited && <span className="text-text-muted">· edited</span>}
        <span className="text-text-muted ml-auto whitespace-nowrap">
          {formatPostedAt(latest.postedAt)}
        </span>
      </div>
      {replyParent !== null && <ReplyQuote parent={replyParent} />}
      {messages.map((m) =>
        m.rawText.length > 0 ? (
          <p
            key={m.id}
            className="mt-1 text-sm text-text whitespace-pre-wrap break-words"
          >
            <DiscordMarkdown text={m.rawText} />
          </p>
        ) : null,
      )}
      {showFooter && (
        <div className="mt-1 flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2 text-xs text-text-muted">
            {attachments.map((a, i) => (
              // Attachments are positional within a message bundle; the
              // url alone isn't a stable React key (Discord uploaders
              // can attach the same asset twice). Bundle index keeps
              // each chip distinct without leaking ordering details
              // back into the data shape.
              <AttachmentChip key={`${a.url}#${i}`} attachment={a} />
            ))}
            {polls.map((m) => (
              <span key={m.id} title="Poll" className="inline-flex items-center gap-1">
                <PollIcon className="h-3 w-3" />
                <span>{m.poll!.question}</span>
              </span>
            ))}
            {row.reactions.map((r) => (
              <ReactionPill key={r.identifier} reaction={r} />
            ))}
          </div>
          {pollVoteDetail !== undefined && (
            <div className="text-xs text-text-muted">{pollVoteDetail}</div>
          )}
        </div>
      )}
    </div>
  );
}
